import {
    type ComponentType,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
} from 'react';
import { type IconProps } from '@phosphor-icons/react';
import { Checkbox, CheckboxVariant } from '@/components/Checkbox';
import { Tag } from '@/components/Tag';
import { Thumbnail } from '@/components/Thumbnail';
import type { ThumbnailQuickViewProps } from '@/components/Thumbnail';
import { cn } from '@/lib/utils';
import {
    isDirectMiddleClick,
    preventMiddleClickAutoscroll,
} from '@/utils/mouse';

export type CardOrientation = 'vertical' | 'horizontal';
export type CardSize = 'default' | 'narrow';
export type CardState = 'default' | 'hover' | 'active';

export interface CardMetadataItem {
    icon: ComponentType<IconProps>;
    text: string;
    title?: string;
    textClassName?: string;
}

interface CardProps {
    children?: ReactNode;
    title?: string;
    subtitle?: ReactNode;
    belowTitleSlot?: ReactNode;
    thumbnailImageSrc?: string;
    thumbnailMedia?: ReactNode;
    thumbnailImageUsesAuth?: boolean;
    thumbnailCenterIcon?: ReactNode;
    thumbnailQuickView?: ThumbnailQuickViewProps;
    thumbnailLicensePlate?: string;
    metadata?: CardMetadataItem[];
    tags?: string[];
    topLeftSlot?: ReactNode;
    topRightSlot?: ReactNode;
    bottomLeftSlot?: ReactNode;
    bottomRightSlot?: ReactNode;
    cardTopRightSlot?: ReactNode;
    sideAction?: ReactNode;
    overlay?: ReactNode;
    orientation?: CardOrientation;
    size?: CardSize;
    state?: CardState;
    loading?: boolean;
    checked?: boolean;
    selectable?: boolean;
    className?: string;
    dataEventId?: string;
    onCheckedChange?: (checked: boolean) => void;
    onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}

const CARD_VERTICAL_THUMBNAIL_HEIGHT = {
    default: 'h-[155px] panels:h-[181px]',
    narrow: 'h-[130px] panels:h-[156px]',
};

const CARD_HORIZONTAL_ROW =
    'flex-col items-stretch gap-2 p-1 pb-2 @[280px]:h-[116px] @[280px]:flex-row @[280px]:items-center @[280px]:gap-2.5 @[280px]:p-2';
const CARD_HORIZONTAL_THUMBNAIL =
    'aspect-video w-full rounded-[6px] @[280px]:aspect-auto @[280px]:h-[100px] @[280px]:w-[154px] @[280px]:rounded-[4px]';
const CARD_HORIZONTAL_TEXT = 'gap-2 px-2 @[280px]:gap-3 @[280px]:px-0';

const CARD_SURFACE_WITH_HOVER =
    'bg-grey-600 transition-colors hover:bg-surface-hover-strong';

function getSurfaceClass(state: CardState, checked: boolean): string {
    if (checked) {
        return 'bg-grey-550 ring-1 ring-inset ring-grey-300';
    }
    if (state === 'active') {
        return 'bg-grey-550';
    }
    if (state === 'hover') {
        return 'bg-grey-550';
    }
    return CARD_SURFACE_WITH_HOVER;
}

function renderThumbnailCornerSlots({
    loading,
    topLeftSlot,
    topRightSlot,
    bottomLeftSlot,
    bottomRightSlot,
}: {
    loading: boolean;
    topLeftSlot?: ReactNode;
    topRightSlot?: ReactNode;
    bottomLeftSlot?: ReactNode;
    bottomRightSlot?: ReactNode;
}) {
    if (loading) {
        return null;
    }
    return (
        <>
            {topLeftSlot && (
                <div className="absolute left-1 top-1 z-10">{topLeftSlot}</div>
            )}
            {topRightSlot && (
                <div className="absolute right-1 top-1 z-10">
                    {topRightSlot}
                </div>
            )}
            {bottomLeftSlot && (
                <div className="absolute bottom-1 left-1 z-10">
                    {bottomLeftSlot}
                </div>
            )}
            {bottomRightSlot && (
                <div className="absolute bottom-1 right-1 z-10">
                    {bottomRightSlot}
                </div>
            )}
        </>
    );
}

function handleActionKeyDown(
    event: KeyboardEvent<HTMLDivElement>,
    onClick?: (event: MouseEvent<HTMLDivElement>) => void,
) {
    if (!onClick) {
        return;
    }

    const target = event.target as HTMLElement;
    const isInteractiveElement =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable;

    if (isInteractiveElement) {
        return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.currentTarget.click();
    }
}

function getInteractiveProps(
    onClick?: (event: MouseEvent<HTMLDivElement>) => void,
) {
    if (!onClick) {
        return {
            onClick: undefined,
            role: undefined,
            tabIndex: undefined,
            onKeyDown: undefined,
            onAuxClick: undefined,
            onMouseDown: undefined,
        };
    }

    return {
        onClick,
        role: 'button' as const,
        tabIndex: 0,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) =>
            handleActionKeyDown(event, onClick),
        onAuxClick: (event: MouseEvent<HTMLDivElement>) => {
            if (isDirectMiddleClick(event)) onClick(event);
        },
        onMouseDown: preventMiddleClickAutoscroll,
    };
}

export function Card(props: CardProps) {
    const interactiveProps = getInteractiveProps(props.onClick);

    if (props.children !== undefined) {
        return (
            <div
                className={cn(
                    'relative w-full rounded-[10px]',
                    CARD_SURFACE_WITH_HOVER,
                    props.onClick ? 'cursor-pointer' : 'cursor-default',
                    props.className,
                )}
                data-event-id={props.dataEventId}
                {...interactiveProps}
            >
                {props.children}
            </div>
        );
    }

    const {
        title,
        subtitle,
        belowTitleSlot,
        thumbnailImageSrc,
        thumbnailMedia,
        thumbnailImageUsesAuth = true,
        thumbnailCenterIcon,
        thumbnailQuickView,
        thumbnailLicensePlate,
        metadata = [],
        tags = [],
        topLeftSlot,
        topRightSlot,
        bottomLeftSlot,
        bottomRightSlot,
        cardTopRightSlot,
        sideAction,
        overlay,
        orientation = 'vertical',
        size = 'default',
        state = 'default',
        loading = false,
        checked = false,
        selectable = false,
        className,
        dataEventId,
        onCheckedChange,
        onClick,
    } = props;

    const isVertical = orientation === 'vertical';
    const isNarrow = size === 'narrow';
    const cardClassName = cn(
        loading ? 'bg-grey-600' : getSurfaceClass(state, checked),
        onClick ? 'cursor-pointer' : 'cursor-default',
        className,
    );
    const clickInteractionProps = getInteractiveProps(onClick);

    if (isVertical) {
        return (
            <div
                className={cn(
                    'relative w-full h-[274px] panels:h-[333px] rounded-[10px] p-1 pb-3',
                    'flex flex-col items-start gap-3',
                    cardClassName,
                )}
                data-event-id={dataEventId}
                {...clickInteractionProps}
            >
                <div
                    className={cn(
                        'relative w-full shrink-0 overflow-hidden rounded-[6px]',
                        loading ? 'bg-grey-600' : 'bg-grey-550',
                        isNarrow
                            ? CARD_VERTICAL_THUMBNAIL_HEIGHT.narrow
                            : CARD_VERTICAL_THUMBNAIL_HEIGHT.default,
                    )}
                >
                    {loading ? (
                        <div className="h-full w-full animate-pulse bg-grey-550" />
                    ) : (
                        <Thumbnail
                            size="fill"
                            imageSrc={thumbnailImageSrc}
                            media={thumbnailMedia}
                            imageUsesAuth={thumbnailImageUsesAuth}
                            centerIcon={thumbnailCenterIcon}
                            quickView={thumbnailQuickView}
                            licensePlate={thumbnailLicensePlate}
                        />
                    )}
                    {renderThumbnailCornerSlots({
                        loading,
                        topLeftSlot,
                        topRightSlot,
                        bottomLeftSlot,
                        bottomRightSlot,
                    })}
                    {!loading && overlay}
                </div>

                <div
                    className={cn(
                        'flex w-full flex-1 flex-col px-2',
                        isNarrow ? 'gap-3' : 'gap-2',
                    )}
                >
                    <div
                        className={cn(
                            'flex w-full items-start',
                            isNarrow ? 'gap-4' : 'gap-2',
                        )}
                    >
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                            {loading ? (
                                <div
                                    className={cn(
                                        'animate-pulse rounded-[4px] bg-grey-550',
                                        isNarrow
                                            ? 'h-6 w-[65%]'
                                            : 'h-7 w-[46%]',
                                    )}
                                />
                            ) : (
                                <>
                                    {subtitle && (
                                        <span className="text-xs text-grey-200 truncate">
                                            {subtitle}
                                        </span>
                                    )}
                                    {title && (
                                        <h3
                                            className={cn(
                                                'text-white-100',
                                                isNarrow
                                                    ? 'truncate text-md-medium leading-6'
                                                    : 'line-clamp-1 panels:line-clamp-2 text-lg-medium leading-7',
                                            )}
                                        >
                                            {title}
                                        </h3>
                                    )}
                                    {belowTitleSlot && (
                                        <div className="flex flex-wrap items-center gap-1">
                                            {belowTitleSlot}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        {!loading && selectable ? (
                            <div
                                className="flex-shrink-0"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Checkbox
                                    checked={checked}
                                    onChange={(value) =>
                                        onCheckedChange?.(value === true)
                                    }
                                    variant={CheckboxVariant.WHITE}
                                    className="h-4 w-4"
                                    wrapperClassName="flex-shrink-0"
                                />
                            </div>
                        ) : (
                            !loading && (
                                <div className="flex-shrink-0">
                                    {sideAction}
                                </div>
                            )
                        )}
                    </div>

                    {loading ? (
                        <div className="flex w-full flex-col gap-2">
                            <div className="h-5 w-full animate-pulse rounded-[4px] bg-grey-550" />
                            <div className="h-5 w-full animate-pulse rounded-[4px] bg-grey-550" />
                        </div>
                    ) : (
                        metadata.length > 0 && (
                            <div
                                className={cn(
                                    'flex w-full flex-col',
                                    isNarrow ? 'gap-2' : 'gap-3',
                                )}
                            >
                                {metadata.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <div
                                            key={`${item.text}-${index}`}
                                            className="flex min-w-0 items-center gap-1.5 text-sm"
                                        >
                                            <Icon
                                                size={14}
                                                weight="regular"
                                                className="shrink-0 text-grey-200"
                                            />
                                            <span
                                                className={cn(
                                                    'truncate text-grey-200',
                                                    item.textClassName,
                                                )}
                                                title={item.title ?? item.text}
                                            >
                                                {item.text}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    )}

                    {!loading && tags.length > 0 && (
                        <div className="flex max-h-6 flex-wrap content-start gap-1 overflow-hidden panels:max-h-12">
                            {tags.map((tagLabel, index) => (
                                <Tag key={`${tagLabel}-${index}`}>
                                    {tagLabel}
                                </Tag>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn('relative w-full rounded-[5px]', cardClassName)}
            data-event-id={dataEventId}
            {...clickInteractionProps}
        >
            <div className={cn('flex w-full', CARD_HORIZONTAL_ROW)}>
                <div
                    className={cn(
                        'relative shrink-0 overflow-hidden',
                        loading ? 'bg-grey-600' : 'bg-grey-550',
                        CARD_HORIZONTAL_THUMBNAIL,
                    )}
                >
                    {loading ? (
                        <div className="h-full w-full animate-pulse bg-grey-550" />
                    ) : (
                        <Thumbnail
                            size="fill"
                            imageSrc={thumbnailImageSrc}
                            media={thumbnailMedia}
                            imageUsesAuth={thumbnailImageUsesAuth}
                            centerIcon={thumbnailCenterIcon}
                            quickView={
                                thumbnailQuickView
                                    ? { ...thumbnailQuickView, compact: true }
                                    : undefined
                            }
                            licensePlate={thumbnailLicensePlate}
                        />
                    )}
                    {renderThumbnailCornerSlots({
                        loading,
                        topLeftSlot,
                        topRightSlot,
                        bottomLeftSlot,
                        bottomRightSlot,
                    })}
                    {!loading && overlay}
                </div>

                <div
                    className={cn(
                        'flex min-w-0 flex-1 flex-col justify-center self-stretch',
                        CARD_HORIZONTAL_TEXT,
                    )}
                >
                    <div className="flex min-w-0 flex-col gap-1">
                        {loading ? (
                            <>
                                <div className="h-[18px] w-[80%] animate-pulse rounded-[4px] bg-grey-550" />
                                <div className="h-5 w-full animate-pulse rounded-[4px] bg-grey-550" />
                            </>
                        ) : (
                            <>
                                {subtitle && (
                                    <span className="truncate text-xs text-grey-200">
                                        {subtitle}
                                    </span>
                                )}
                                {title && (
                                    <h3 className="truncate text-sm-medium leading-5 text-white-100">
                                        {title}
                                    </h3>
                                )}
                                {belowTitleSlot && (
                                    <div className="flex flex-wrap items-center gap-1">
                                        {belowTitleSlot}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex flex-col gap-1">
                            <div className="h-[18px] w-[60%] animate-pulse rounded-[4px] bg-grey-550" />
                            <div className="h-[18px] w-[60%] animate-pulse rounded-[4px] bg-grey-550" />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1">
                            {metadata.slice(0, 2).map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <div
                                        key={`${item.text}-${index}`}
                                        className="flex min-w-0 items-center gap-1.5 text-xs text-grey-300"
                                    >
                                        <Icon
                                            size={14}
                                            weight="regular"
                                            className="shrink-0"
                                        />
                                        <span
                                            className="truncate"
                                            title={item.title ?? item.text}
                                        >
                                            {item.text}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            {!loading && cardTopRightSlot && (
                <div className="absolute right-2 top-2 z-20">
                    {cardTopRightSlot}
                </div>
            )}
        </div>
    );
}
